// Approved review items 08 and 21 share customer-scoped in-memory drafts.
// Saves below update this review only; provider persistence and queue advancement remain runtime work.
//
// W32 (2026-10-20) real-contact wiring: on a real HubSpot session
// (useReviewIdentity().isPreview === false) the identity row, breadcrumb
// and hero come from GET /v1/contacts/:id, and the horse list is seeded
// from GET /v1/contacts/:id/horses through the same useContactHorses
// hook Current Action already uses. The DUKE / SARAH fixture is reachable
// ONLY in preview (explicit ?preview= or the dev mock session). While the
// horses read is in flight the screen shows a loading card, never the
// fixture seed. No horse write route exists yet, so the editor Save
// stays local and is labelled "Saved in this review only, not written
// to HubSpot".
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { FileShortcuts } from '../_shared/FileShortcuts.js';
import { UpNext } from '../_shared/UpNext.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { HorseProfile } from './components/HorseProfile.js';
import { ProductsDailyRoutine } from './components/ProductsDailyRoutine.js';
import { HorseGoals } from './components/HorseGoals.js';
import { HorseProfileHistory } from './components/HorseProfileHistory.js';
import { NextQuestionPrompt } from './components/NextQuestionPrompt.js';
import { HorseProfileCompleteness } from './components/HorseProfileCompleteness.js';
import { ColicCoverageSummary } from './components/ColicCoverageSummary.js';
import { HorseRelationships } from './components/HorseRelationships.js';
import { HorseEditorDialog } from './components/HorseEditorDialog.js';
import { type EditorMode } from './mockData.js';
import { createHorseReviewState, createHorseReviewStateFromRecords, discardHorseDraft, emptyHorse, hasHorseDrafts, horseDraftDirty,
  HORSE_REVIEW_KEY, NEW_HORSE_ID, openHorseDraft, REAL_SAVE_LABEL, saveHorseDraft, type HorseDraft, type HorseEditorValues,
  type HorseRecordDetail, type HorseReviewState } from './reviewState.js';
import { getReviewReturnTarget, useReviewState } from '../_shared/ReviewState.js';
import { SARAH } from '../../data/mockData.js';
import { useReviewIdentity } from '../_shared/useReviewIdentity.js';
import { useContactHorses } from '../_shared/useContactCollections.js';
import { useRep } from '../_shared/useRep.js';
import { useScreenRuntime } from '../CurrentActionReview/runtime-clients.js';
import { RuntimeApiError } from '../../api/runtimeClient.js';
import './styles.css';
import './app.css';

export const NO_HORSE_COPY = 'No horse on file yet. Ask during the call and add it.';

export function HorsesReviewScreen(): ReactNode {
  const params = useParams();
  const customerId = params.customerId || SARAH.id;
  const requestedHorseId = params.horseId;

  const location = useLocation();
  const navigate = useNavigate();
  const identity = useReviewIdentity(customerId);
  const isPreview = identity.isPreview;
  const horsesRead = useContactHorses(customerId);
  const repState = useRep();
  const [review, setReview] = useReviewState<HorseReviewState>(customerId, HORSE_REVIEW_KEY, () => createHorseReviewState(customerId));

  // W32: on a real session replace whatever seeded the store (the fixture
  // when a preview or another tab touched it first) with the HubSpot
  // horses exactly once. Local drafts made after seeding are untouched.
  useEffect(() => {
    if (isPreview || horsesRead.status !== 'ready') return;
    setReview(state => state.seed === 'hubspot' ? state : createHorseReviewStateFromRecords(customerId, horsesRead.items as HorseRecordDetail[]));
  }, [isPreview, horsesRead.status, horsesRead.items, customerId, setReview]);
  const horsesSeeded = isPreview || review.seed === 'hubspot';

  // W2A upper lane: when a signed session is live, fetch the horse
  // timeline from the api server. Preview-fixture mode skips the call.
  // Errors are surfaced inline below the identity row and do not block
  // the editor flow (horses on the preview catalog remain editable so
  // reps can still draft intake data when the backend is slow).
  const runtime = useScreenRuntime();
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  useEffect(() => {
    if (!runtime) { setRuntimeError(null); return; }
    let cancelled = false;
    runtime.signed
      .request<{ horses?: unknown[] }>({
        method: 'GET',
        path: `/v1/contacts/${encodeURIComponent(customerId)}/horses`,
      })
      .then(() => { if (!cancelled) setRuntimeError(null); })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof RuntimeApiError && err.kind === 'NOT_FOUND') return; // route optional
        setRuntimeError(err instanceof Error ? err.message : 'Unknown error');
      });
    return () => { cancelled = true; };
  }, [runtime, customerId]);
  const [editor, setEditor] = useState({ customerId, horseId: review.selectedId, open: false, mode: 'profile' as EditorMode });
  const [feedback, setFeedback] = useState({ customerId, message: '', error: '', result: '' });
  const tell = (message: string, error = '', result = '') => setFeedback({ customerId, message, error, result });
  const active = horsesSeeded ? (review.horses.find(horse => horse.id === review.selectedId) ?? emptyHorse(customerId)) : emptyHorse(customerId);
  const draft = review.drafts[editor.horseId];
  const editorHorse = review.horses.find(horse => horse.id === editor.horseId) ?? emptyHorse(customerId);
  const hasDrafts = hasHorseDrafts(review);
  const historyStatus = feedback.customerId === customerId ? feedback.message : '';
  const editorOpen = editor.open && editor.customerId === customerId;
  const noHorseOnFile = !isPreview && horsesSeeded && review.horses.length === 0 && !review.drafts[NEW_HORSE_ID];
  const firstName = identity.firstName;
  const customerName = identity.customer.fullName;
  const repName = repState.status === 'ready' ? repState.rep.fullName : 'This rep';

  useEffect(() => {
    if (requestedHorseId) setReview(state => state.horses.some(horse => horse.id === requestedHorseId)
      ? { ...state, selectedId: requestedHorseId } : state);
  }, [customerId, requestedHorseId, review.seed, setReview]);

  const closeEditor = () => setEditor(value => ({ ...value, open: false }));
  const openEditor = (mode: EditorMode) => {
    const horseId = mode === 'add' ? NEW_HORSE_ID : review.selectedId;
    if (horseId === NEW_HORSE_ID && mode !== 'add' && mode !== 'profile') {
      tell('Save the new horse before editing other details.'); return;
    }
    setReview(state => openHorseDraft(state, horseId));
    setEditor({ customerId, horseId, open: true, mode: horseId === NEW_HORSE_ID ? 'add' : mode });
    tell('');
  };
  const updateDraft = (patch: Partial<HorseEditorValues>) => {
    if (editor.customerId !== customerId) return;
    setReview(state => {
      const current = state.drafts[editor.horseId];
      if (!current || current.customerId !== customerId) return state;
      return { ...state, drafts: { ...state.drafts, [editor.horseId]: { ...current, values: { ...current.values, ...patch } } } };
    });
    tell('');
  };
  const updateMetadata = (patch: Pick<Partial<HorseDraft>, 'effectiveDate' | 'note'>) => {
    if (editor.customerId !== customerId) return;
    setReview(state => {
      const current = state.drafts[editor.horseId];
      return current ? { ...state, drafts: { ...state.drafts, [editor.horseId]: { ...current, ...patch } } } : state;
    });
    tell('');
  };
  const handleSave = (returnAfter: boolean) => {
    if (editor.customerId !== customerId) return;
    try {
      const saved = isPreview
        ? saveHorseDraft(review, editor.horseId, editor.mode)
        : saveHorseDraft(review, editor.horseId, editor.mode, undefined, `${repName} · This review only`);
      setReview(saved.state);
      setEditor(value => ({ ...value, horseId: saved.horseId, mode: value.mode === 'add' ? 'profile' : value.mode }));
      const message = saved.changed ? (isPreview ? 'Saved in this preview · Jamie Dorsey' : REAL_SAVE_LABEL) : 'No changes to save.';
      tell(message, '', message);
      if (returnAfter) {
        closeEditor();
        const target = getReviewReturnTarget(location.state, customerId);
        navigate(target.path, { state: target.state });
      }
    } catch (error) { tell('', error instanceof Error ? error.message : 'The horse could not be saved.'); }
  };
  const discard = () => {
    setReview(state => discardHorseDraft(state, editor.horseId));
    closeEditor();
    tell('Unsaved changes discarded. Saved horse history is retained.');
  };

  // Hero copy. Preview keeps the approved design strings; a real session
  // derives every line from the HubSpot profile + horses read.
  const hero = useMemo(() => {
    if (isPreview) {
      return {
        heading: `Review ${active.name}'s profile`,
        what: `Complete ${active.name}'s profile for Sarah`,
        whyNow: `A clear profile keeps every call personal. ${active.name} is missing a few details.`,
        nextAction: `Confirm the daily amount, Sarah's goal and ${active.name}'s birth date.`,
        detail: 'Record customer answers in their own words.',
      };
    }
    if (!horsesSeeded) {
      return horsesRead.status === 'error'
        ? { heading: 'Horses could not load', what: 'Waiting on HubSpot', whyNow: `The horses read failed (${horsesRead.errorCode || 'ERR'}).`, nextAction: 'Reload, then ask about the horse on the call.', detail: undefined }
        : { heading: 'Loading horses…', what: 'Waiting on HubSpot', whyNow: 'The horse list has not loaded yet.', nextAction: 'Wait for the record, then review.', detail: undefined };
    }
    if (review.horses.length === 0) {
      return {
        heading: `Add ${firstName}'s first horse`,
        what: 'No horse on file yet',
        whyNow: 'HubSpot has no horse on this contact. Every call is more personal with the horse’s name.',
        nextAction: 'Ask which horse they are feeding and add it.',
        detail: 'Saved in this review only until the horse write route lands.',
      };
    }
    const missing = active.completeness.map(item => item.label.toLowerCase());
    return {
      heading: `Review ${active.name}'s profile`,
      what: `Complete ${active.name}'s profile for ${firstName}`,
      whyNow: missing.length
        ? `A clear profile keeps every call personal. ${active.name} is missing: ${missing.join(', ')}.`
        : `A clear profile keeps every call personal. ${active.name}'s profile has no gaps HubSpot can see.`,
      nextAction: `Confirm the daily amount, ${firstName}'s goal and ${active.name}'s birth date.`,
      detail: 'Record customer answers in their own words.',
    };
  }, [isPreview, horsesSeeded, horsesRead.status, horsesRead.errorCode, review.horses.length, active, firstName]);

  const horseCards: ReactNode = (
    <div className="horse-layout">
      <div className="horse-main">
        <HorseProfile horse={active} onEdit={openEditor} customerName={customerName} customerFirstName={firstName}
          gender={isPreview ? undefined : active.gender ?? undefined} />
        <ProductsDailyRoutine horse={active} onEdit={openEditor} customerFirstName={firstName} />
        <HorseGoals horse={active} onEdit={openEditor} customerFirstName={firstName} />
        <HorseProfileHistory horse={active} historyStatus={historyStatus} events={active.history} />
      </div>
      <aside className="horse-side">
        <NextQuestionPrompt horse={active} onEdit={openEditor} customerFirstName={firstName} />
        <HorseProfileCompleteness horse={active} onEdit={openEditor} />
        <ColicCoverageSummary horse={active} customerId={customerId} />
        <HorseRelationships horse={active} onOpen={() => navigate(`/relationships/${encodeURIComponent(customerId)}`)} />
      </aside>
    </div>
  );

  return (
    <WorkspaceChrome screen="horses" rail="my-work">
      <div className="breadcrumbs">
        <div>
          My work
          <span className="slash">/</span>
          <strong>{customerName}</strong>
        </div>
        {isPreview ? (
          <span className="preview">Design preview &middot; Sample horse profile</span>
        ) : identity.statusPill ? (
          <span className="preview">{identity.statusPill}</span>
        ) : null}
      </div>
      <PriorityFocus
        ariaLabel="HORSE PROFILE · NEXT STEP"
        eyebrow="HORSE PROFILE · NEXT STEP"
        heading={hero.heading}
        what={{ label: 'WHAT', heading: hero.what, iconId: 'message' }}
        whyNow={{ label: 'WHY NOW', body: hero.whyNow, iconId: 'clock' }}
        nextAction={{ label: 'NEXT ACTION', body: hero.nextAction, detail: hero.detail, iconId: 'target' }}
        ownership={{
          label: 'OWNERSHIP',
          iconId: 'people',
          detail: isPreview ? 'Primary account owner. Coverage preserves ownership.' : identity.ownerDetail,
        }}
        {...(isPreview ? {} : { owner: identity.owner, showRepriority: false })}
      />
      <IdentityRow customer={identity.customer} badgeLabel={identity.badgeLabel} sinceLabel={identity.sinceLabel} />
      <FileShortcuts customerId={customerId} firstName={identity.customer.firstName} />
      <CustomerTabs customerId={customerId} active="horses" />

      <section className="horse-review-controls" aria-label="Horse selection">
        <label>Horse on this account<select value={review.selectedId}
          onChange={event => { setReview(state => ({ ...state, selectedId: event.target.value })); closeEditor(); tell(''); }}>
          {(horsesSeeded ? review.horses : []).map(horse => <option value={horse.id} key={horse.id}>{horse.name}{review.drafts[horse.id] && horseDraftDirty(review.drafts[horse.id], horse.id) ? ' · Unsaved changes' : ''}</option>)}
          {review.drafts[NEW_HORSE_ID] && <option value={NEW_HORSE_ID}>New horse · Unsaved</option>}
        </select></label>
        <button type="button" className="outlined" onClick={() => openEditor('add')} disabled={!horsesSeeded}>Add a horse</button>
        {hasDrafts && <span className="needs-pill">Unsaved horse changes</span>}
      </section>
      {!isPreview && (
        <p className="horse-review-result" data-testid="horses-local-save-note">
          Edits here stay in this review only. Nothing is written to HubSpot yet.
        </p>
      )}
      {historyStatus && <p className="horse-review-result" role="status">{historyStatus}</p>}
      {runtimeError && <p className="horse-review-result" role="alert" data-testid="horses-runtime-error">Horse data could not load: {runtimeError}</p>}

      {!horsesSeeded ? (
        horsesRead.status === 'error' ? (
          <section className="card" role="alert" data-testid="horses-read-error" style={{ padding: 24, margin: '12px 0' }}>
            <strong>Horses could not load</strong>
            <p style={{ margin: '6px 0 0' }}>The horses read failed ({horsesRead.errorCode || 'ERR'}). Ask about the horse on the call and try again later.</p>
          </section>
        ) : (
          <section className="card" role="status" aria-live="polite" data-testid="horses-read-loading" style={{ padding: 24, margin: '12px 0' }}>
            <strong>Loading horses…</strong>
            <p style={{ margin: '6px 0 0' }}>Live content will replace this placeholder when the data arrives.</p>
          </section>
        )
      ) : noHorseOnFile ? (
        <section className="card" data-testid="horses-empty" style={{ padding: 24, margin: '12px 0' }}>
          <strong>{NO_HORSE_COPY}</strong>
          <p style={{ margin: '6px 0 12px' }}>HubSpot has no horse on {customerName}'s record. Add the horse during the call; it stays in this review until the horse write route lands.</p>
          <button type="button" className="blue-action" onClick={() => openEditor('add')}>Add a horse</button>
        </section>
      ) : horseCards}

      <UpNext customerId={customerId} />
      <ActionFooter
        disabled={hasDrafts}
        noteWhenDisabled="Save or discard the unfinished horse changes first"
        noteWhenEnabled="Horse details reviewed · Ready to continue"
        summaryBody="Keep the customer's next action clear before finishing."
        onFinish={() => tell(isPreview
          ? 'Horse details reviewed in this preview. Live queue advancement is not connected.'
          : 'Horse details reviewed. Live queue advancement is not connected yet.')}
      />

      {draft && <HorseEditorDialog
        key={customerId + ':' + editor.horseId}
        open={editorOpen}
        mode={editor.mode}
        horse={editorHorse}
        draft={draft}
        dirty={horseDraftDirty(draft, editor.horseId)}
        error={feedback.customerId === customerId ? feedback.error : ''}
        result={feedback.customerId === customerId ? feedback.result : ''}
        onClose={closeEditor}
        onChange={updateDraft}
        onMetadataChange={updateMetadata}
        onSave={handleSave}
        onDiscard={discard}
        {...(isPreview ? {} : { scopeNote: `${REAL_SAVE_LABEL}. Saved facts and unsaved drafts stay in this review until reload. No customer record, order or subscription is changed.` })}
      />}
    </WorkspaceChrome>
  );
}

export default HorsesReviewScreen;
